import { FC } from "react"
import { Link } from "react-router-dom"
import { ShieldAlert, X } from "lucide-react"
import ApproveDomainButton from "../approve-domain-button"
import DeclineDomainButton from "../decline-domain-button"
import { usePendingEmbedDomains } from "../../hooks"

const MAX_HOSTS_SHOWN = 3
const MAX_EMBEDS_SHOWN = 1

/**
 * Tells the customer when a website is being blocked from loading their embed
 * because its URL is not approved yet — the case that otherwise arrives as a
 * support email asking us to whitelist a test URL — and lets them approve it
 * from here in one click.
 *
 * Renders nothing when there is nothing blocked (or it has been dismissed), so
 * it is safe to mount unconditionally.
 */
const BlockedDomainsBanner: FC = () => {
  const {
    undismissed,
    dismiss,
    approve,
    approvingHost,
    decline,
    decliningHost
  } = usePendingEmbedDomains()

  if (undismissed.length === 0) return null

  const shown = undismissed.slice(0, MAX_HOSTS_SHOWN)
  const remaining = undismissed.length - shown.length
  const isSingle = undismissed.length === 1

  return (
    <div
      className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-4"
      role="alert"
    >
      <ShieldAlert
        size={20}
        className="mt-0.5 shrink-0 text-amber-500"
        aria-hidden="true"
      />

      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-medium text-amber-900">
          {isSingle
            ? "A new website is trying to load your embed and is blocked"
            : `${undismissed.length} new websites are trying to load your embeds and are blocked`}
        </h3>
        <p className="mt-1 text-sm text-amber-800">
          {isSingle ? "It is" : "They are"} not on your approved URL list, so
          your rate table will not load there. Approve to let it through, or
          hide it to stop being asked — hiding leaves it blocked.
        </p>

        <ul className="mt-3 space-y-2">
          {shown.map(domain => (
            <li
              key={domain.host}
              className="flex items-center justify-between gap-3 rounded border border-amber-200 bg-white px-3 py-2"
            >
              <div className="min-w-0">
                <span className="block truncate text-sm font-medium text-base-dark">
                  {domain.host}
                </span>
                {/* Which embed it was blocked on. The app key is how the
                    customer identifies an embed in their own snippet, and
                    what support asks them for. */}
                {!!domain.integrations?.length && (
                  <span className="block truncate text-xs text-neutral-600">
                    {domain.integrations
                      .slice(0, MAX_EMBEDS_SHOWN)
                      .map(({ id, title, app_key }) => (
                        <span key={id} className="mr-2">
                          {title}
                          {app_key && (
                            <span className="ml-1 font-mono text-neutral-400">
                              {app_key}
                            </span>
                          )}
                        </span>
                      ))}
                    {domain.integrations.length > MAX_EMBEDS_SHOWN &&
                      `+${domain.integrations.length - MAX_EMBEDS_SHOWN} more`}
                  </span>
                )}
                <span className="block truncate text-xs text-neutral-500">
                  Approves {domain.suggested_pattern}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <ApproveDomainButton
                  domain={domain}
                  approve={approve}
                  approvingHost={approvingHost}
                  disabled={!!decliningHost}
                />
                <DeclineDomainButton
                  domain={domain}
                  decline={decline}
                  decliningHost={decliningHost}
                  disabled={!!approvingHost}
                />
              </div>
            </li>
          ))}
        </ul>

        <Link
          to="/approve-integrations"
          className="mt-3 inline-block text-sm font-medium text-amber-900 underline decoration-dashed underline-offset-4 hover:text-amber-950"
        >
          {remaining > 0
            ? `See ${remaining} more and manage approved URLs`
            : "Manage approved URLs"}
        </Link>
      </div>

      <button
        type="button"
        onClick={dismiss}
        className="shrink-0 rounded p-1 text-amber-500 hover:bg-amber-100 hover:text-amber-700"
        aria-label="Dismiss"
      >
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  )
}

export default BlockedDomainsBanner
